<script setup lang="ts">
/** [#e03fdbc2] Статья-инструкция. Текст приходит из src/content/articles.json (база знаний). */
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { ArrowRight, ChevronRight } from 'lucide-vue-next';
import { ARTICLES } from '@/seo/head';
import PublicShell from './landing/PublicShell.vue';
import { usePublicPage } from './landing/usePublicPage';

const { locale, copy, to } = usePublicPage();
const route = useRoute();

const article = computed(() => ARTICLES.find((a) => a.slug === route.params.slug) ?? null);
const text = computed(() => article.value?.i18n[locale.value] ?? null);
/** Сквозной номер шага для ссылок из разметки HowTo. */
function stepOffset(section: { title: string }): number {
  const all = text.value?.sections ?? [];
  return all.slice(0, all.findIndex((x) => x.title === section.title)).reduce((n, x) => n + x.steps.length, 0);
}

const related = computed(() =>
  ARTICLES.filter((a) => a.group === article.value?.group && a.slug !== article.value?.slug)
    .slice(0, 4)
    .map((a) => ({ path: `/articles/${a.slug}`, title: a.i18n[locale.value].title })),
);
</script>

<template>
  <PublicShell>
    <main v-if="text && article" class="lp-wrap lp-narrow lp-page">
      <nav class="lp-crumbs" :aria-label="copy.articles.home">
        <RouterLink :to="to('/')">{{ copy.articles.home }}</RouterLink>
        <ChevronRight :size="14" />
        <RouterLink :to="to('/articles')">{{ copy.articles.title }}</RouterLink>
        <ChevronRight :size="14" /><span>{{ copy.articles.groups[article.group] }}</span>
      </nav>
      <article>
        <header class="lp-page-head">
          <h1>{{ text.title }}</h1>
          <p>{{ text.description }}</p>
        </header>
        <section v-for="s in text.sections" :key="s.title" class="lp-card lp-article">
          <h2>{{ text.sections.length > 1 ? s.title : copy.articles.stepsTitle }}</h2>
          <p v-if="s.when" class="lp-when"><b>{{ copy.articles.when }}.</b> {{ s.when }}</p>
          <ol>
            <li v-for="(step, i) in s.steps" :id="`step-${stepOffset(s) + i + 1}`" :key="i"><span class="lp-num">{{ i + 1 }}</span><p>{{ step }}</p></li>
          </ol>
        </section>
      </article>

      <template v-if="related.length">
        <h2 class="lp-group">{{ copy.articles.related }}</h2>
        <ul class="lp-list">
          <li v-for="r in related" :key="r.path">
            <RouterLink :to="to(r.path)" class="lp-row"><div><h3>{{ r.title }}</h3></div><ChevronRight :size="18" /></RouterLink>
          </li>
        </ul>
      </template>

      <aside class="lp-card lp-help">
        <h2>{{ copy.articles.helpTitle }}</h2>
        <p>{{ copy.articles.helpText }}</p>
        <RouterLink :to="to('/')" class="lp-link">DWISH MDM<ArrowRight :size="16" /></RouterLink>
      </aside>
    </main>
  </PublicShell>
</template>
